import {
  Button,
  DevDetailsButton,
  Divider,
  Drawer,
  Flex,
  FlexProps,
  HelpHover,
  IconButton,
  Image,
  InputContainer,
  Panel,
  PencilSquareIcon,
  Popover,
  Text,
  TextField,
  TrashIcon,
  toaster,
} from 'components'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import { isEqual, startCase } from 'lodash-es'
import { Fragment, forwardRef, useEffect, useState } from 'react'
import { EventMoment, MomentType } from 'types'
import { updateDoc, useEntevatePermission } from 'utils'
import { useTemplates } from 'utils/templateUtils'
import manageIcon from '../../../assets/moment-icons/manage.svg'
import { useEventInfo } from '../../../pages/EventInfoPage'
import { useEvent } from '../../../utils/eventUtils'
import { activityMomentOptions, createMoment, momentOptions } from '../../../utils/momentUtils'
import TemplatePreviewButton from '../../TemplatePreviewButton'
import ManageActivityMoment from './ManageActivityMoment'
import ManageExplorerMoment from './ManageExplorerMoment'
import ManagePublicMoment from './ManagePublicMoment'
import MomentsTemplateSelect from './MomentsTemplateSelect'

export default function MomentsButton() {
  const { moments, updateMoments } = useEventInfo()
  const { confirm } = useAlert()
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const { event } = useEvent()

  const [drafts, setDrafts] = useState<EventMoment[]>([])

  const selectedId = typeof moments === 'number' ? moments : undefined
  const isDirty = !isEqual(drafts, event?.moments)

  useEffect(() => {
    if (!event) return

    setDrafts(event.moments)
    if (event.moments.length === 0) updateMoments(true)
  }, [event])

  const updateSelectedId = (id: number | undefined) => updateMoments(id ?? true)

  async function handleDone() {
    try {
      updateMoments(false)
      if (!isDirty) return

      await updateDoc(user!.id, 'events', { id: event!.id, moments: drafts })
    } catch (error) {
      console.error(error)
      toaster.create({ title: 'Error saving moments', type: 'error' })
      updateMoments(true)
    }
  }

  async function handleCancel() {
    if (isDirty) {
      const confirmed = await confirm('Discard changes?')
      if (!confirmed) return

      setDrafts(event!.moments)
    }

    updateSelectedId(undefined)
    updateMoments(false)
  }

  async function createFirstMoments(template: PartialTemplate) {
    const explorerMoment = createMoment([], 'explorer', template)
    const publicMoment = createMoment([explorerMoment], 'public', template)
    setDrafts([explorerMoment, publicMoment])
  }

  function handleNew(moment: EventMoment) {
    setDrafts([...drafts, moment])
    updateSelectedId(moment.id)
  }

  function handleDelete() {
    setDrafts(drafts.filter(m => m.id !== selectedId))
    updateSelectedId(undefined)
  }

  function handleUpdate(updatedMoment: EventMoment) {
    const updatedMoments = drafts.map(m => (m.id === selectedId ? updatedMoment : m))
    setDrafts(updatedMoments)
    updateSelectedId(undefined)
  }

  if (workspace?.status.value !== 'active') return

  return (
    <>
      <Panel>
        <Button height="100%" variant="ghost" onClick={() => updateMoments(true)}>
          Moments{drafts.length ? ` (${drafts.length})` : ''}
        </Button>
      </Panel>

      <Drawer
        open={moments !== false}
        onClose={handleCancel}
        header={
          <Flex alignItems="center" gap={2}>
            <Text textStyle="bodyMedium">Moments</Text>
            <HelpHover
              positioning={{ placement: 'right-start' }}
              text="Use Moments to set up and track different activations at your event. Each Moment helps you: capture leads, assign templates, and measure performance across your event."
            />
          </Flex>
        }
        headerIcon={<Image src={manageIcon} alt="Manage Moments" />}
        bodyProps={{ gap: 3 }}
        footer={
          <Flex justifyContent="flex-end" gap={2}>
            {isDirty && (
              <Button variant="ghost" onClick={handleCancel} disabled={selectedId != null}>
                Cancel
              </Button>
            )}
            <Button palette="primary" onClick={handleDone} disabled={selectedId != null}>
              {isDirty ? 'Save' : 'Done'}
            </Button>
          </Flex>
        }
      >
        {drafts.length === 0 ? (
          <>
            <Text textStyle="bodyLarge">Let's get started</Text>
            <Text textStyle="bodySmall">Select a template to create your first Moments:</Text>
            <TemplateList onClick={createFirstMoments} />
          </>
        ) : (
          <>
            <Text textStyle="bodySmall">{drafts.length} Moments</Text>

            {drafts.map((moment, index) =>
              moment.id === selectedId ? (
                <EditMoment
                  key={moment.id}
                  moment={moment}
                  moments={drafts}
                  onDelete={!event?.sessionCount && index > 1 ? handleDelete : undefined}
                  onDone={handleUpdate}
                />
              ) : (
                <ViewMoment
                  disabled={selectedId != null}
                  isDraft={!event?.moments.some(m => m.id === moment.id)}
                  key={moment.id}
                  moment={moment}
                  onClick={() => updateSelectedId(moment.id)}
                />
              )
            )}

            <InputContainer label="Add New">
              <NewButton
                onNew={(type, template) => handleNew(createMoment(drafts, type, template))}
              />
            </InputContainer>
          </>
        )}
      </Drawer>
    </>
  )
}

interface EditMomentProps {
  moments: EventMoment[]
  moment: EventMoment
  onDone: (moment: EventMoment) => void
  onDelete?: () => void
}

function EditMoment({ moments, moment, onDone, onDelete }: EditMomentProps) {
  const [draft, setDraft] = useState<EventMoment>(moment)
  const isDirty = !isEqual(draft, moment)

  const { template, type, title } = draft
  const duplicateTitle = moments.some(m => m.id !== moment.id && m.title === title.trim())

  return (
    <Container direction="column" gap={2}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text textStyle="bodyMedium" fontWeight="semibold">
          {type === 'public' ? 'Self Registration' : startCase(type)}
        </Text>
        <Flex alignItems="center" gap={2}>
          <DevDetailsButton
            items={[
              { label: 'Template', type: 'db', id: moment.template.id, collection: 'templates' },
            ]}
          />
          {onDelete && (
            <IconButton aria-label="Remove Moment" icon={<TrashIcon />} onClick={onDelete} />
          )}
        </Flex>
      </Flex>

      <TextField
        autoFocus
        helperText={duplicateTitle ? 'Title must be unique' : ''}
        isError={duplicateTitle}
        required
        label="Name"
        onChange={e => setDraft(s => ({ ...s, title: e.target.value }))}
        onFocus={e => e.target.select()}
        value={title}
      />

      {(() => {
        switch (type) {
          case 'activity':
            return <ManageActivityMoment value={draft} onChange={setDraft} />
          case 'explorer':
            return <ManageExplorerMoment value={draft} onChange={setDraft} />
          case 'public':
            return <ManagePublicMoment value={draft} onChange={setDraft} />
        }
      })()}

      <MomentsTemplateSelect
        value={template.id}
        onChange={({ id }) => setDraft(s => ({ ...s, template: { ...template, id } }))}
      />

      <Flex justifyContent="flex-end" gap={2}>
        {isDirty && (
          <Button variant="ghost" onClick={() => onDone(moment)}>
            Cancel
          </Button>
        )}
        <Button
          disabled={!title || duplicateTitle}
          palette="primary"
          onClick={() => onDone({ ...draft, title: title.trim() })}
        >
          Done
        </Button>
      </Flex>
    </Container>
  )
}

interface ViewMomentProps {
  moment: EventMoment
  disabled?: boolean
  isDraft: boolean
  onClick?: () => void
}

function ViewMoment({ moment, disabled, isDraft, onClick }: ViewMomentProps) {
  const { event } = useEvent()
  const { type, id, title } = moment

  let { iconSrc, label } = momentOptions.find(option => option.type === type) || {}
  switch (type) {
    case 'activity':
      const option = activityMomentOptions.find(o => o.type === moment.activityType)
      iconSrc = option?.iconSrc
      label = `Activity • ${option?.label}`
      break
    case 'public':
      label = 'Self Registration • Public QR Code'
      break
  }

  return (
    <Container alignItems="center" justifyContent="space-between">
      <Flex gap={3}>
        <Image height="18px" src={iconSrc} />
        <Flex direction="column" gap={1}>
          <Text lineHeight={1}>{title}</Text>
          <Text textStyle="bodySmall" opacity={0.6}>
            {label}
          </Text>
        </Flex>
      </Flex>

      <Flex gap={1}>
        {type === 'explorer' && (
          <TemplatePreviewButton
            device="ipad"
            eventId={event?.id}
            disabled={isDraft}
            momentId={id}
            palette="primary"
            previewTitle="Event Preview"
          />
        )}
        <IconButton
          aria-label="Edit Moment"
          icon={<PencilSquareIcon />}
          onClick={onClick}
          disabled={disabled}
        />
      </Flex>
    </Container>
  )
}

interface NewButtonProps {
  onNew: (type: MomentType, template: PartialTemplate) => void
}

function NewButton({ onNew }: NewButtonProps) {
  const [entevatePermission] = useEntevatePermission()
  const [newType, setNewType] = useState<MomentType>()

  let filterOptions = momentOptions
  if (entevatePermission !== 'entevate')
    filterOptions = filterOptions.filter(option => option.type !== 'activity')

  function handleNew(template: PartialTemplate) {
    onNew(newType!, template)
    setNewType(undefined)
  }

  return (
    <Popover
      header="Select Template"
      open={!!newType}
      positioning={{ placement: 'bottom', sameWidth: true }}
      onClose={() => setNewType(undefined)}
      trigger={
        <Container p={2} gap={2} justifyContent="space-between" alignItems="center">
          {filterOptions.map((option, index) => (
            <Fragment key={index}>
              {index !== 0 && <Divider alignSelf="stretch" height="auto" orientation="vertical" />}
              <Flex
                flexDirection="column"
                gap={3}
                alignItems="center"
                onClick={() => setNewType(option.type)}
                cursor="pointer"
                _hover={{ background: 'gray.50' }}
                borderRadius="sm"
                flex={1}
                p={2}
              >
                <Image height="18px" src={option.iconSrc} />
                <Text whiteSpace="nowrap" textStyle="bodySmall" textAlign="center">
                  {option.label}
                </Text>
              </Flex>
            </Fragment>
          ))}
        </Container>
      }
    >
      <TemplateList onClick={handleNew} />
    </Popover>
  )
}

interface PartialTemplate {
  id: string
  title: string
}

function TemplateList({ onClick }: { onClick?: (template: PartialTemplate) => void } = {}) {
  const { templates } = useTemplates()

  return (
    <Flex as="ul" overflow="hidden" direction="column">
      {templates?.map((t, index) => (
        <Fragment key={t.id}>
          {index > 0 && <Divider />}
          <Flex
            as="li"
            p={2}
            onClick={() => onClick?.(t)}
            direction="column"
            overflow="hidden"
            _hover={{ background: 'gray.200' }}
            cursor="pointer"
          >
            <Text>{t.title}</Text>
            <Text lineClamp={2} textStyle="bodyXSmall" opacity={0.6}>
              {t.description.en}
            </Text>
          </Flex>
        </Fragment>
      ))}
    </Flex>
  )
}

const Container = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
  return (
    <Flex
      ref={ref}
      background="white"
      border="1px solid #DEE2F4"
      borderRadius="md"
      p={4}
      {...props}
    />
  )
})
